const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/** e.g. 22nd July 2027 from YYYY-MM-DD (local calendar parts, no UTC shift). */
export function formatUkOrdinalLongDate(iso: string | null | undefined): string {
  const s = String(iso ?? "").trim();
  if (!s) return "—";
  const part = s.split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return "—";
  const [ys, ms, ds] = part.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (!y || m < 1 || m > 12 || d < 1 || d > 31) return "—";
  return `${d}${ordinalSuffix(d)} ${MONTHS[m - 1]} ${y}`;
}

export function passSubtitleForColor(color: string | null | undefined): string {
  const c = String(color ?? "").trim().toLowerCase();
  switch (c) {
    case "green":
      return "Authorised to Assess";
    case "red":
      return "Authorised to Accompany";
    case "yellow":
      return "PTS (Personal Track Safety) competent";
    case "blue":
      return "Operational Rules competent";
    case "black":
      return "Route learning";
    default:
      return "Driving cab pass";
  }
}

/** Routes / scope line on the pass (dates appear under VALID separately). */
export function routesDisplayText(validityScope: string | null | undefined): string {
  const raw = String(validityScope ?? "");
  return raw.trim() ? raw : "—";
}

/** Calendar YYYY-MM-DD in Europe/London (rail ops); falls back to UTC if Intl unavailable. */
export function cabPassCalendarToday(): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** Pass has ended: valid_to is a YYYY-MM-DD strictly before London calendar today. */
export function cabPassIsExpired(validTo: string | null | undefined): boolean {
  const today = cabPassCalendarToday();
  const vt = String(validTo ?? "")
    .trim()
    .split("T")[0];
  if (!vt || !/^\d{4}-\d{2}-\d{2}$/.test(vt)) return false;
  return today > vt;
}

export function validityBlockText(
  validityScope: string | null | undefined,
  validFrom: string | null | undefined,
  validTo: string | null | undefined
): string {
  const raw = String(validityScope ?? "");
  if (raw.trim()) return raw;
  const vf = formatUkOrdinalLongDate(validFrom ?? "");
  const vt = formatUkOrdinalLongDate(validTo ?? "");
  if (vf !== "—" && vt !== "—") return `${vf} to ${vt}`;
  if (vt !== "—") return `Until ${vt}`;
  if (vf !== "—") return `From ${vf}`;
  return "—";
}
